import { i18n } from '@lt/localization';
import { createSelector } from '@reduxjs/toolkit';
import { getIsHotelOnly } from 'src/store/view/selectors';

import { visaRiskSelector } from '../../selectors/extras';
import { getCountryToText } from '../../selectors/hotel';
import { VISA_TYPES, VISA_SERVICES } from './constants';
import type { PackageVisaStateType } from './types';

const getVisaInfo = (state): PackageVisaStateType => state.visa;

export const getVisaType = createSelector(
  getVisaInfo,
  // лучше вынести наверное как дефолтное значение в initialState
  (state) => state.type || VISA_TYPES.NO_VISA,
);
export const getVisaPrice = createSelector(getVisaInfo, (state) => state.price);

export const getVisaDocsPrice = createSelector(
  getVisaInfo,
  (state) => state.docPrice,
);

export const hasVisaSupport = createSelector(
  getVisaInfo,
  (state) => state.policy === 'we_do',
);

export const getVisaRulesLink = createSelector(
  getVisaInfo,
  (state) => state.rules || '',
);

export const getIsVisaPurchasable = createSelector(
  getVisaInfo,
  (state) => state.purchasable,
);

const getVisaCustomText = createSelector(getVisaInfo, (state) => state.text);

export const getVisaInfoText = createSelector(
  [getVisaType, getVisaCustomText, getCountryToText],
  (visaType, visaText, countryTo): string => {
    if (!visaType) {
      return '';
    }
    if (visaText) {
      return visaText;
    }
    return i18n.t('checkout.visa.forEntry', { countryTo });
  },
);

export const getAvailableVisaServices = createSelector(
  [getVisaType, hasVisaSupport, getIsVisaPurchasable],
  (visaType, visaSupport, isVisaPurchasable) => {
    if (
      visaType === VISA_TYPES.NO_VISA ||
      visaType === VISA_TYPES.ON_ARRIVAL_VISA
    ) {
      // Виза не нужна/Виза по прилету: нет визовой поддержки.
      return null;
    }
    const services = Object.values(VISA_SERVICES).filter(({ id }) => {
      if (id === VISA_SERVICES.VISA_FULL.id) {
        return isVisaPurchasable && visaSupport;
      }
      return true;
    });
    return services.length ? services : null;
  },
);

export const getServicePrice = (
  state: unknown,
  id: string,
  tourId: string,
): number => {
  const visaDocPrice = getVisaDocsPrice(state);
  const visaPrice = getVisaPrice(state);
  const isHotelOnly = getIsHotelOnly(state);
  const visaRisk = !isHotelOnly ? visaRiskSelector(state, tourId) : undefined;
  switch (id) {
    case VISA_SERVICES.VISA_DOCS.id:
      return visaDocPrice;
    case VISA_SERVICES.VISA_FULL.id:
      // Если есть услуга "Визовый риск", ее стоимость добавляется в цену оформления визы.
      if (visaRisk) {
        return visaDocPrice + visaRisk.price;
      }
      return visaPrice;
    case VISA_SERVICES.NO_VISA.id:
    default:
      return 0;
  }
};

export const getServiceName = (
  state: unknown,
  id: string,
  label: string,
  tourId: string,
): string => {
  const isHotelOnly = getIsHotelOnly(state);
  const visaRisk = !isHotelOnly ? visaRiskSelector(state, tourId) : undefined;
  switch (id) {
    case VISA_SERVICES.VISA_FULL.id:
      return visaRisk ? `${label} ${i18n.t('checkout.visa.risk')}` : label;
    case VISA_SERVICES.VISA_DOCS.id:
    case VISA_SERVICES.NO_VISA.id:
    default:
      return label;
  }
};
